import { observer } from 'mobx-react-lite';
import { IconButton } from '@fluentui/react/lib/Button';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import { AttachmentPreviewMode } from 'owa-sxs-store';
import { trace } from 'owa-trace';
import * as React from 'react';
import { SurfaceAction } from '../../types/SurfaceAction';
import {
    handleComponentOnBlur,
    handleComponentOnFocus,
} from '../../utils/handleAttachmentViewFocus';
import { getIconName, getIconTitle } from './getIconProperties';
import { getShouldHideSurfaceActionButton } from './getShouldHideSurfaceActionButton';
import { performSurfaceAction } from './performSurfaceAction';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';

export interface AttachmentSurfaceActionButtonProps {
    action: SurfaceAction;
    attachmentViewState: AttachmentFullViewState;
    isForImageView: boolean;
    previewState: AttachmentPreviewMode;
    customClassName?: string;
    shouldNotHandleOnBlur?: boolean;
    parentItemId: ClientItemId;
}

const styles = require('./AttachmentSurfaceActionButton.scss');
const classNames = require('classnames/bind').bind(styles);

// This component is responsible for showing the attachment download button or add to calendar button for calendar attachment
export default observer(function AttachmentSurfaceActionButton(
    props: AttachmentSurfaceActionButtonProps
) {
    const targetWindow = React.useContext(ProjectionContext);
    const keyDown = (event: React.KeyboardEvent<unknown>) => {
        if (event.keyCode === KeyCodes.enter || event.keyCode === KeyCodes.space) {
            trace.info('AttachmentSurfaceActionButton.keyDown enter|space');
            event.stopPropagation();
            performSurfaceAction(
                props.action,
                props.attachmentViewState,
                props.parentItemId,
                targetWindow
            );
        }
    };
    const onClickButton = (ev: any) => {
        trace.info('AttachmentSurfaceActionButton.onclick');
        ev.stopPropagation();
        performSurfaceAction(
            props.action,
            props.attachmentViewState,
            props.parentItemId,
            targetWindow
        );
    };
    const handleOnFocus = () =>
        handleComponentOnFocus(props.attachmentViewState, props.isForImageView);
    const handleOnBlur = () => {
        if (!props.shouldNotHandleOnBlur) {
            handleComponentOnBlur(props.attachmentViewState, props.isForImageView);
        }
    };
    const { action, attachmentViewState, customClassName, isForImageView, previewState } = props;
    const shouldHideButton = getShouldHideSurfaceActionButton(action, attachmentViewState);
    if (!shouldHideButton) {
        let classNameString: string;
        const attachmentStyle = getAttachmentStyle(previewState);
        if (isForImageView) {
            const conditionalStyles = {
                extraRightPadding: isThirdButtonWithDownloadEnabled(action, attachmentViewState),
            };
            classNameString = classNames([
                styles.actionButton,
                styles.actionButtonForImageView,
                customClassName,
                conditionalStyles,
            ]);
        } else if (attachmentViewState.isReadOnly) {
            classNameString = classNames([styles.actionButton, attachmentStyle, customClassName]);
        } else {
            classNameString = classNames([
                styles.actionButton,
                attachmentStyle,
                styles.actionButtonForDocViewInCompose,
                customClassName,
            ]);
        }
        const controlIconName: ControlIcons = getIconName(action, attachmentViewState.attachmentId);
        const controlIconTitle: string = getIconTitle(action, attachmentViewState.attachmentId);
        return (
            <IconButton
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                onKeyDown={keyDown}
                ariaLabel={controlIconTitle}
                title={controlIconTitle}
                onClick={onClickButton}
                className={classNameString}
                iconProps={{
                    iconName: controlIconName,
                    styles: {
                        root: styles.icon,
                    },
                }}
            />
        );
    } else {
        return null;
    }
});

// We need to make space for the download button if it is there.
function isThirdButtonWithDownloadEnabled(
    action: SurfaceAction,
    attachmentViewState: AttachmentFullViewState
): boolean {
    const isValidAction =
        action === SurfaceAction.saveToCloud ||
        action === SurfaceAction.editAndReply ||
        action === SurfaceAction.openNewTabAndSaveToCloud ||
        action === SurfaceAction.openAndEdit;
    return (
        isValidAction &&
        !getShouldHideSurfaceActionButton(SurfaceAction.download, attachmentViewState)
    );
}

function getAttachmentStyle(previewState: AttachmentPreviewMode): string {
    switch (previewState) {
        case AttachmentPreviewMode.Edit:
            return styles.edit;
        case AttachmentPreviewMode.NotSelectedAndSaving:
            return styles.notSelectedAndSaving;
        case AttachmentPreviewMode.OtherSaving:
            return styles.attachmentView;
        case AttachmentPreviewMode.Selected:
            return styles.selected;
        case AttachmentPreviewMode.SelectedAndPending:
            return styles.selectedAndPending;
        case AttachmentPreviewMode.SelectedAndSaving:
            return styles.selectedAndSaving;
        default:
            return styles.default;
    }
}
